import { Button, Checkbox, CircularProgress, FormControlLabel, FormHelperText } from '@mui/material';
import { Form, FormikProps } from 'formik';
import FormLabel from '@forms/Components/FormLabel';
import PhoneMaskedInput from '@forms/Components/PhoneMaskInput';
import AppTextInput from '@forms/Components/TextInput';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { SendPudlReceiptForm, formatOrderNo } from './types';
import useCurrentTerminal from '@hooks/useCurrentTerminal';
import { useModalProvider } from '@components/Modals/ModalContext';
import useFetch from '@hooks/useFetch';
import { useEffect } from 'react';

import styles from './SendPudleReceipt.module.css';

type SendPuDlReceiptFormProps = FormikProps<SendPudlReceiptForm> & {
	id: string;
	legId: number;
	/** Set when sending the receipts of multiple legs; each leg's receipt goes to its own driver. */
	batchLegs?: DispatchLeg[];
	/** The legs belong to different drivers, so only the download is offered. */
	printOnly?: boolean;
};

/**
 * Form component for the Send Pudl Receipt Modal
 */
export default function SendPickupDeliveryForm(props: SendPuDlReceiptFormProps) {
	const { number } = useCurrentTerminal();
	const { queueModalAnnouncement } = useModalProvider();
	const { fetchFile, isLoading, isError, errorMessage } = useFetch();
	const { batchLegs, printOnly } = props;
	const handleDownloadClick = () => {
		if (batchLegs) {
			// Every selected leg's receipt, merged into a single PDF.
			void fetchFile(
				`/${number}/pickupdeliveryreceipts/${String(props.legId)}`,
				'PUT',
				JSON.stringify({
					action: 'GetReceiptsBatch',
					data: {
						legIds: batchLegs.map((leg) => leg.legId),
						includeAllLegs: props.values.incAllLegs,
						includeCustInfo: props.values.incCustInfo
					}
				}),
				'application/pdf'
			);
			return;
		}

		void fetchFile(
			`/${number}/pickupdeliveryreceipts/${String(props.legId)}?includeAllLegs=${String(
				props.values.incAllLegs
			)}&includeCustInfo=${String(props.values.incCustInfo)}`
		);
	};

	useEffect(() => {
		if (isError) {
			queueModalAnnouncement({
				message: errorMessage,
				severity: 'error'
			});
		}
	}, [isError, queueModalAnnouncement, errorMessage]);

	return (
		<Form
			id={props.id}
			className={styles.form}
		>
			<div className={styles.row}>
				<div className={styles['include-checkboxes']}>
					<FormControlLabel
						label={
							<FormLabel
								text='Include All Legs'
								bold
							/>
						}
						labelPlacement='start'
						control={
							<Checkbox
								name='incAllLegs'
								onChange={props.handleChange}
								checked={props.values.incAllLegs}
							/>
						}
					/>
					<FormControlLabel
						label={
							<FormLabel
								text='Include Customer Info'
								bold
							/>
						}
						labelPlacement='start'
						control={
							<Checkbox
								name='incCustInfo'
								onChange={props.handleChange}
								checked={props.values.incCustInfo}
							/>
						}
					/>
				</div>

				<Button
					variant='outlined'
					onClick={handleDownloadClick}
					disabled={isLoading}
					className={styles['download-btn']}
				>
					{!isLoading ? 'Download' : <CircularProgress size={24} />}
				</Button>
			</div>

			{batchLegs && <FormHelperText className={styles.hint}>Orders: {batchLegs.map(formatOrderNo).join(', ')}</FormHelperText>}

			{printOnly && (
				<FormHelperText className={styles.hint}>
					The selected orders are assigned to different drivers, so their receipts can only be downloaded, not emailed or texted.
				</FormHelperText>
			)}

			{!printOnly && (
				<fieldset className={styles.section}>
					<legend className={styles.legend}>
						<FormControlLabel
							className={styles['fieldset-chk-lbl']}
							label={
								<FormLabel
									text='Email'
									bold
									error={Boolean(props.touched.sendText && props.errors.sendText)}
									errorText={props.touched.sendText && props.errors.sendText && props.errors.sendText}
								/>
							}
							labelPlacement='start'
							control={
								<Checkbox
									name='sendEmail'
									onChange={props.handleChange}
									checked={props.values.sendEmail}
								/>
							}
						/>
					</legend>
	
					{batchLegs && (
						<FormHelperText className={styles.hint}>
							Each leg's receipt is emailed to the driver. Change the Email Address to send every receipt only there instead, and enter a Copy
							To address to also receive one email with all {batchLegs.length} receipts. Editing the subject or message below sends that text
							with every receipt instead.
						</FormHelperText>
					)}
	
					<AppTextInput
						type='email'
						name='emailAddr'
						className={styles.email}
						value={props.values.emailAddr}
						onChange={props.handleChange}
						onBlur={props.handleBlur}
						disabled={!props.values.sendEmail}
						error={Boolean(props.touched.emailAddr && props.errors.emailAddr)}
						customProps={{
							labelText: 'Email Address',
							multiple: true,
							errorText: props.touched.emailAddr && props.errors.emailAddr ? props.errors.emailAddr : undefined,
							onClear: () => {
								void props.setFieldTouched('emailAddr', true);
								void props.setFieldValue('emailAddr', '');
							}
						}}
					/>
	
					{batchLegs && (
						<AppTextInput
							type='email'
							name='copyEmailAddr'
							className={styles.email}
							value={props.values.copyEmailAddr}
							onChange={props.handleChange}
							onBlur={props.handleBlur}
							disabled={!props.values.sendEmail}
							error={Boolean(props.touched.copyEmailAddr && props.errors.copyEmailAddr)}
							customProps={{
								labelText: 'Copy To Email Address',
								multiple: true,
								errorText: props.touched.copyEmailAddr && props.errors.copyEmailAddr ? props.errors.copyEmailAddr : undefined,
								onClear: () => {
									void props.setFieldTouched('copyEmailAddr', true);
									void props.setFieldValue('copyEmailAddr', '');
								}
							}}
						/>
					)}
	
					<AppTextInput
						type='text'
						name='emailSubject'
						className={styles.emailSubject}
						value={props.values.emailSubject}
						onChange={props.handleChange}
						onBlur={props.handleBlur}
						disabled={!props.values.sendEmail}
						error={Boolean(props.touched.emailSubject && props.errors.emailSubject)}
						customProps={{
							labelText: 'Subject',
							errorText: props.errors.emailSubject,
							onClear: () => {
								void props.setFieldValue('emailSubject', '');
							}
						}}
					/>
	
					<AppTextInput
						type='text'
						name='emailBody'
						className={styles.message}
						value={props.values.emailBody}
						onChange={props.handleChange}
						onBlur={props.handleBlur}
						disabled={!props.values.sendEmail}
						error={Boolean(props.touched.emailBody && props.errors.emailBody)}
						customProps={{
							labelText: 'Message',
							errorText: props.errors.emailBody,
							onClear: () => {
								void props.setFieldValue('emailBody', '');
							},
							textArea: true
						}}
					/>
				</fieldset>
			)}

			{!printOnly && (
				<fieldset className={styles.section}>
					<legend className={styles.legend}>
						<FormControlLabel
							className={styles['fieldset-chk-lbl']}
							label={
								<FormLabel
									text='Text Message'
									bold
									error={Boolean(props.touched.sendText && props.errors.sendText)}
									errorText={props.touched.sendText && props.errors.sendText && props.errors.sendText}
								/>
							}
							labelPlacement='start'
							control={
								<Checkbox
									name='sendText'
									onChange={props.handleChange}
									checked={props.values.sendText}
								/>
							}
						/>
					</legend>
	
					{batchLegs && (
						<FormHelperText className={styles.hint}>
							Each leg's receipt is texted to its driver. Enter a phone number to also receive all {batchLegs.length} receipt messages. Editing
							the message below sends that text to every driver instead.
						</FormHelperText>
					)}
	
					<PhoneMaskedInput
						inputProps={{
							name: 'cellPhone',
							value: props.values.cellPhone,
							onChange: (event) => {
								void props.setFieldTouched('cellPhone', false);
								void props.setFieldValue('cellPhone', event.target.value);
							},
							onBlur: () => {
								void props.setFieldTouched('cellPhone', true, true);
							},
							disabled: !props.values.sendText
						}}
						labelText={batchLegs ? 'Copy To Phone #' : 'Phone #'}
						error={Boolean(props.touched.cellPhone && props.errors.cellPhone)}
						errorText={props.touched.cellPhone && props.errors.cellPhone ? props.errors.cellPhone : undefined}
						onClear={() => {
							void props.setFieldTouched('cellPhone', true);
							void props.setFieldValue('cellPhone', '');
						}}
					/>
	
					<AppTextInput
						type='text'
						name='textBody'
						className={styles['text-message']}
						value={props.values.textBody}
						onChange={props.handleChange}
						onBlur={props.handleBlur}
						disabled={!props.values.sendText}
						error={Boolean(props.touched.textBody && props.errors.textBody)}
						customProps={{
							labelText: 'Message',
							errorText: props.errors.textBody,
							onClear: () => {
								void props.setFieldValue('textBody', '');
							},
							textArea: true
						}}
					/>
				</fieldset>
			)}
		</Form>
	);
}
